'use client';

import { Button } from '@ironhack/design-system2/components/button';
import { Card, type CardProps } from '@ironhack/design-system2/components/card';
import { Flex } from '@ironhack/design-system2/components/flex';
import {
  LinkBox,
  LinkOverlay,
} from '@ironhack/design-system2/components/link-box';
import { Text } from '@ironhack/design-system2/components/text';
import NextLink from 'next/link';
import { type ReactElement, useEffect, useState } from 'react';

import { type BasePageParams, getPageUrl } from '@/app/urls/url-mapping';
import { getPrettyCohort } from '@/lib/utils';
import { type Language } from '@/lib/utils/language';
import { type WebCohort } from '@/types';

type Props = {
  buttonText: string;
  cohort: WebCohort;
  isPriceHidden: boolean | null;
  language: Language;
  params: Pick<BasePageParams, 'language' | 'region'>;
} & CardProps;

export const CohortSelectorCard = (props: Props): ReactElement => {
  const { buttonText, cohort, isPriceHidden, language, params, ...cardProps } =
    props;

  const [useCohortTimezone, setUseCohortTimezone] = useState(true);
  // Use cohort timezone on the server to prevent a hydration error
  useEffect(() => setUseCohortTimezone(false), [setUseCohortTimezone]);

  const prettyCohort = getPrettyCohort(cohort, language, {
    useCohortTimezone,
  });

  const cohortId = cohort.external_ids.salesforce || '';
  const applyUrl = getPageUrl('apply', { ...params, cohortId });

  return (
    <Card
      as={LinkBox}
      boxShadow="0px 0px 4px rgb(0 0 0 / 8%)"
      gap={2}
      padding={2}
      position="relative"
      size={['full', null, '2xl']}
      {...cardProps}
    >
      <Text textStyle="lBold">
        {`${prettyCohort.startDate} - ${prettyCohort.endDate}`}
      </Text>

      <Flex direction="column" gap={0.5}>
        <Text color="text.secondary" textStyle="mBold">
          {`${prettyCohort.weekDays.join(
            ` ${cohort.format === 'ft' ? '-' : '&'} `
          )}: ${prettyCohort.weekDayStartTime} - ${
            prettyCohort.weekDayEndTime
          } (${prettyCohort.timezone})`}
        </Text>
        {cohort.format === 'pt' && (
          <Text color="text.secondary" textStyle="mBold">
            {`${prettyCohort.weekendDays.join(' & ')}: ${
              prettyCohort.weekendStartTime
            } - ${prettyCohort.weekendEndTime} (${prettyCohort.timezone})`}
          </Text>
        )}
        <Text color="text.secondary" textStyle="mBold">
          {`${prettyCohort.modality} - ${prettyCohort.language}`}
        </Text>
      </Flex>

      {!isPriceHidden && (
        <Flex className="price" gap={1}>
          <Text textStyle="mBold">{`${prettyCohort.discountPrice}`}</Text>
          {prettyCohort.price !== prettyCohort.discountPrice && (
            <Text as="s" color="text.tertiary" textStyle="mBold">
              {`${prettyCohort.price}`}
            </Text>
          )}
        </Flex>
      )}

      {applyUrl && (
        <LinkOverlay alignSelf="start" as={NextLink} href={applyUrl}>
          <Button size="s">{buttonText}</Button>
        </LinkOverlay>
      )}
    </Card>
  );
};
